import React, { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Page } from 'tracking';

import { AppContextProvider } from '@contexts/AppContext';
import { useEventTracking, useServerSideRendering } from '@hooks';

import DeepResearchSection from '../DeepResearchSection';
import ProArticles from './ProArticles';
import ProCTA from './ProCTA';
import ProFocusOnAI from './ProFocusOnAI';
import ProHero from './ProHero';
import ProPerk from './ProPerk';
import ProQuote from './ProQuote';
import ProSubscription from './ProSubscription';
import ProTeams from './ProTeams';
import { ProPerks } from './constants';
import { ProProps } from './interfaces';

const ProNewsletter = lazy(() => import('./Newsletter/index'));
const queryClient = new QueryClient();

const Pro = ({
  env,
  initialCurrentUser,
  isSEOBot,
  isWebview,
  proArticles,
  proOfferCode,
}: ProProps) => {
  const { Track } = useEventTracking({
    page: Page.AboutPro,
  });
  const hydrated = useServerSideRendering();
  const isProSubscriberNotBot = initialCurrentUser?.is_pro_subscriber && !isSEOBot;

  return (
    <AppContextProvider env={env} initialCurrentUser={initialCurrentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <Track>
          <main className="px-16 sm:px-28">
            <div className="m-auto max-w-[1040px] overflow-hidden" id="pro-landing-page">
              <ProHero
                initialCurrentUser={initialCurrentUser}
                isProSubscriberNotBot={isProSubscriberNotBot}
                proOfferCode={proOfferCode}
              />
              <ProSubscription isProSubscriberNotBot={isProSubscriberNotBot} />
              <DeepResearchSection isProSubscriberNotBot={isProSubscriberNotBot} />
              <ProFocusOnAI isProSubscriberNotBot={isProSubscriberNotBot} />
              <ProPerk
                className="mt-48 border-0 border-t border-solid border-grayscale-beige pt-48 lg:px-40"
                isProSubscriberNotBot={isProSubscriberNotBot}
                {...ProPerks.access}
              />
              <ProPerk
                className="mt-48 border-0 border-t border-solid border-grayscale-beige pt-48 sm:flex-row-reverse lg:px-40"
                isProSubscriberNotBot={isProSubscriberNotBot}
                {...ProPerks.events}
              />
              {hydrated && isProSubscriberNotBot && (
                <Suspense>
                  <ProNewsletter />
                </Suspense>
              )}
              {!isProSubscriberNotBot && (
                <ProCTA
                  initialCurrentUser={initialCurrentUser}
                  isProSubscriberNotBot={isProSubscriberNotBot}
                  proOfferCode={proOfferCode}
                />
              )}
              <ProQuote isProSubscriberNotBot={isProSubscriberNotBot} />
              <ProArticles proArticles={proArticles} />
            </div>
          </main>
          <ProTeams />
        </Track>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default Pro;
